import React, { useState } from 'react';
import { usePortfolio } from '../../context/PortfolioContext';
import { ContactMessage } from '../../domain/ContactMessage';
import { Mail, MapPin, Send, MessageSquare, Facebook, Github } from 'lucide-react';

export const ContactSection: React.FC = () => {
  const { notify } = usePortfolio();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const recruiterPresets = [
    { label: '📅 Schedule an Interview', subject: 'Interview Invitation for Web Developer Role' },
    { label: '📄 Request Full Resume PDF', subject: 'Requesting Full Resume & Project Portfolio' },
    { label: '💼 Discussion on Web Project', subject: 'Inquiry regarding Web Developer Position' },
    { label: '⚡ General Tech Inquiry', subject: 'General Technical Inquiry' }
  ];

  const handlePresetClick = (presetSubject: string) => {
    setFormData((prev) => ({
      ...prev,
      subject: presetSubject,
      message: `Hi Zenerol,\n\nI reviewed your portfolio and would like to connect regarding "${presetSubject}". Let's discuss available opportunities.\n\nBest regards,`
    }));
    notify('Preset message applied to contact form!', 'info');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Using OOP Domain Model for Validation
    const contactMsg = new ContactMessage(formData);
    const validation = contactMsg.validate();

    if (!validation.isValid) {
      setErrors(validation.errors);
      setIsSubmitting(false);
      notify('Please resolve the highlighted form errors.', 'warning');
      return;
    }

    // Simulate clean async submission
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setErrors({});
      notify('Thank you! Your message has been sent successfully. I will get back to you within 24 hours.', 'success');
    }, 1000);
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-title-wrap">
          <div className="section-tag">
            <MessageSquare size={14} />
            <span>Let's Connect</span>
          </div>
          <h2 className="section-title">
            Get In <span>Touch</span>
          </h2>
          <p className="section-subtitle">
            Currently available for full-time Web Developer roles, IT Specialist positions, and contract projects.
          </p>
        </div>

        <div className="contact-layout">
          {/* Contact Information & Presets */}
          <div>
            <div className="contact-info-cards">
              <a href="mailto:lorenzmanillasaldivar@gmail.com" className="contact-card">
                <div className="contact-icon">
                  <Mail size={22} />
                </div>
                <div>
                  <span className="contact-label">Email Direct</span>
                  <div className="contact-value">lorenzmanillasaldivar@gmail.com</div>
                </div>
              </a>

              <a href="https://www.facebook.com/LorenzSaldivars" target="_blank" rel="noreferrer" className="contact-card">
                <div className="contact-icon" style={{ background: 'rgba(24, 119, 242, 0.1)', color: '#1877F2' }}>
                  <Facebook size={22} />
                </div>
                <div>
                  <span className="contact-label">Facebook Profile</span>
                  <div className="contact-value">facebook.com/LorenzSaldivars</div>
                </div>
              </a>

              <a href="https://github.com/Zenerol" target="_blank" rel="noreferrer" className="contact-card">
                <div className="contact-icon" style={{ background: 'rgba(127, 0, 255, 0.1)', color: 'var(--accent-purple)' }}>
                  <Github size={22} />
                </div>
                <div>
                  <span className="contact-label">GitHub Workspace</span>
                  <div className="contact-value">github.com/Zenerol</div>
                </div>
              </a>

              <div className="contact-card">
                <div className="contact-icon" style={{ background: 'rgba(0, 230, 118, 0.1)', color: 'var(--accent-green)' }}>
                  <MapPin size={22} />
                </div>
                <div>
                  <span className="contact-label">Location & Relocation</span>
                  <div className="contact-value">Available Remote & On-Site</div>
                </div>
              </div>
            </div>

            {/* Recruiter Presets */}
            <div style={{ marginTop: '2rem' }}>
              <div className="preset-wrap">
                <span className="preset-title">⚡ Quick Recruiter Templates:</span>
                <div className="preset-pills">
                  {recruiterPresets.map((preset, idx) => (
                    <button
                      key={idx}
                      className="preset-btn"
                      onClick={() => handlePresetClick(preset.subject)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <h3 style={{ fontSize: '1.4rem', color: '#fff', marginBottom: '1.5rem' }}>
              Send a Direct Message
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="e.g. Alex Morgan"
                />
                {errors.name && <span style={{ color: '#ff4d4d', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.name}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="alex@company.com"
                />
                {errors.email && <span style={{ color: '#ff4d4d', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Subject *</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="form-control"
                placeholder="e.g. Web Developer Position Inquiry"
              />
              {errors.subject && <span style={{ color: '#ff4d4d', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.subject}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Message Content *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-control"
                placeholder="Write your message here..."
              ></textarea>
              {errors.message && <span style={{ color: '#ff4d4d', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{errors.message}</span>}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', padding: '0.9rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span>Sending Message...</span>
              ) : (
                <>
                  <Send size={18} />
                  <span>Send Message</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
